/* eslint-disable react/no-unescaped-entities */
import { Link } from 'react-router-dom';
import {
  Layers, Users, Building2, Coins, FileText, Vote, Calendar, ScrollText,
} from 'lucide-react';
import GuidePage from '@/components/library/GuidePage';

const config = {
  path: '/sci',
  slug: 'sci',
  breadcrumbLabel: 'SCI',
  readingTime: '8 min',
  lastUpdated: '18 février 2026',
  seoTitle: 'SCI : statuts, parts et fonctionnement | HÉRITA',
  seoDescription:
    "Comprenez facilement une SCI : statuts, cession de parts, assemblées, gérance, transmission. Analysez vos documents de société civile immobilière avec HÉRITA.",

  hero: {
    eyebrow: 'Guides HÉRITA',
    title: 'Comprendre une SCI',
    subtitle:
      "La SCI (Société Civile Immobilière) est l'un des outils les plus utilisés pour détenir et transmettre un patrimoine immobilier. HÉRITA vous aide à comprendre ses statuts, les cessions de parts, les assemblées et la fiscalité associée.",
  },

  definition: {
    eyebrow: 'Définition',
    title: "Qu'est-ce qu'une SCI\u00A0?",
    paragraphs: [
      <>
        Une SCI — Société Civile Immobilière — est une <em>société</em> dont
        l'objet est de détenir, gérer et parfois louer des biens immobiliers.
        À la place d'être propriétaire en direct, vous êtes{' '}
        <strong className="font-semibold text-[#1A2530]">propriétaire de parts</strong>{' '}
        d'une société qui, elle, possède l'immeuble.
      </>,
      <>
        Elle est créée par un acte notarié ou par acte sous seing privé&nbsp;:
        les <em>statuts</em>. Ils fixent les règles du jeu — qui décide,
        comment, dans quelles conditions on peut entrer ou sortir, comment
        sont réparties les parts, comment elles sont cédées.
      </>,
      <>
        <strong className="font-semibold text-[#1A2530]">Pourquoi créer une SCI&nbsp;?</strong>{' '}
        Trois raisons principales reviennent. Faciliter la transmission&nbsp;:
        donner des parts est plus simple que de morceler un immeuble. Éviter
        l'indivision&nbsp;: la SCI a une vie organisée, contrairement à
        l'indivision qui paralyse parfois. Protéger un patrimoine familial&nbsp;:
        les statuts peuvent imposer un agrément avant toute cession.
      </>,
      <>
        <strong className="font-semibold text-[#1A2530]">Comment fonctionne-t-elle&nbsp;?</strong>{' '}
        Une SCI a un ou plusieurs gérants qui prennent les décisions
        courantes. Les associés se réunissent en assemblée générale au moins
        une fois par an pour approuver les comptes et statuer sur les
        décisions importantes. Chaque associé dispose d'un nombre de voix
        proportionnel à ses parts.
      </>,
      <>
        Au décès d'un associé, ses parts sont en principe transmises à ses
        héritiers&nbsp;: c'est ce qui fait toute la puissance fiscale et
        successorale de l'outil. Selon la rédaction des statuts, les
        héritiers entrent automatiquement ou doivent recevoir un{' '}
        <em>agrément</em> des autres associés.
      </>,
      <>
        Si vous recevez aujourd'hui des statuts de SCI, un acte de cession de
        parts ou un procès-verbal d'assemblée, HÉRITA vous aide à comprendre
        votre place exacte dans la société et le lien avec d'éventuelles
        questions d'<Link to="/indivision" className="text-[#1A2530] font-medium underline underline-offset-[5px] decoration-[#D3CCBE] hover:decoration-[#1A2530]">indivision</Link>
        {' '}ou de succession.
      </>,
    ],
  },

  usage: {
    eyebrow: 'Utilité',
    title: "À quoi sert-elle\u00A0?",
    items: [
      { title: 'Faciliter la transmission', description: "Donner des parts est plus simple, plus progressif et fiscalement plus avantageux que transmettre un immeuble entier." },
      { title: 'Éviter l\'indivision', description: "Une SCI a des règles claires, des organes de décision et une comptabilité. Elle évite la paralysie de l'indivision." },
      { title: 'Protéger un patrimoine', description: "Les statuts peuvent imposer un agrément avant toute cession : aucun étranger ne peut entrer sans accord des associés." },
      { title: 'Organiser la gestion', description: "Un gérant unique prend les décisions courantes. Les grandes décisions se votent en assemblée à la majorité prévue." },
    ],
  },

  content: {
    eyebrow: 'Contenu',
    title: "Que contient un dossier SCI\u00A0?",
    intro: "Plusieurs documents composent la vie d'une SCI. Voici les principaux à savoir lire.",
    cards: [
      { Icon: ScrollText, title: 'Statuts', description: "Les règles fondatrices : objet, capital, parts, agréments, gérance, durée, dissolution." },
      { Icon: Users, title: 'Associés', description: "Liste nominative avec le nombre de parts détenues par chacun et les éventuels démembrements (nue-propriété, usufruit)." },
      { Icon: Building2, title: 'Biens détenus', description: "Description des immeubles, terrains et droits immobiliers appartenant à la société." },
      { Icon: Coins, title: 'Capital social', description: "Montant total du capital, valeur nominale d'une part et règles de modification." },
      { Icon: FileText, title: 'Cession de parts', description: "Acte qui transfère des parts d'un associé à un autre — souvent soumis à agrément préalable." },
      { Icon: Vote, title: 'Procès-verbal d\'AG', description: "Compte rendu des décisions votées : approbation des comptes, modification des statuts, nomination du gérant." },
      { Icon: Calendar, title: 'Comptes annuels', description: "Bilan et résultat de l'exercice écoulé. Permettent de mesurer la santé financière de la société." },
      { Icon: Layers, title: 'Démembrement', description: "Si les parts sont démembrées (nue-propriété + usufruit), les statuts en précisent les conséquences." },
    ],
  },

  checks: {
    eyebrow: 'Vigilance',
    title: 'Les points importants à vérifier',
    intro: "La SCI est un outil souple mais exigeant. Voici les éléments à relire avec soin avant toute décision.",
    items: [
      "Vérifier la clause d'agrément des statuts : qui peut entrer ? Avec quelle majorité ?",
      "Identifier le ou les gérants et l'étendue de leurs pouvoirs.",
      "Confirmer la répartition exacte des parts entre les associés.",
      "Lire les règles de cession : prix, droit de préemption, conditions de retrait.",
      "Vérifier l'option fiscale : impôt sur le revenu (IR) ou impôt sur les sociétés (IS).",
      "Identifier toute clause spécifique sur le décès d'un associé.",
    ],
  },

  autoChecks: {
    eyebrow: 'Sans rien demander',
    title: 'Ce que HÉRITA identifie automatiquement',
    intro: "Dès l'import des statuts ou d'une cession, HÉRITA repère les informations clés.",
    items: [
      'Dénomination, siège, durée de la SCI',
      'Capital social et valeur nominale',
      'Associés et nombre de parts',
      'Gérant(s) et étendue des pouvoirs',
      "Clauses d'agrément et de cession",
      'Régime fiscal (IR ou IS)',
      'Démembrement éventuel des parts',
      'Questions à poser à votre notaire',
    ],
  },

  example: {
    eyebrow: 'Avant · Après',
    title: 'Un exemple simplifié',
    intro: "À gauche, un extrait fictif de statuts de SCI. À droite, ce qu'HÉRITA en aurait expliqué.",
    extract: (
      <>
        «&nbsp;La société dénommée «&nbsp;SCI LES GLYCINES&nbsp;» est constituée
        entre Monsieur Pierre LEROY (60 parts), Madame Hélène LEROY (60 parts)
        et leurs enfants Antoine LEROY (40 parts) et Sophie LEROY (40 parts).
        Le capital social est fixé à 200 000 €, divisé en 200 parts de 1 000
        € chacune. La gérance est confiée à Monsieur Pierre LEROY pour une
        durée illimitée. Toute cession de parts à un tiers est soumise à
        l'agrément des associés réunissant les trois quarts du capital.&nbsp;»
      </>
    ),
    headline: "Ce que cela signifie pour Sophie",
    bullets: [
      { strong: "Quatre associés", rest: " : vos parents (60 parts chacun) et votre frère et vous (40 parts chacun)." },
      { strong: "Vous détenez 20 % du capital", rest: " (40 parts sur 200), pour une valeur nominale de 40 000 €." },
      { strong: "Votre père est gérant", rest: " unique : il prend les décisions courantes au nom de la société." },
      { strong: "Toute cession à un tiers", rest: " requiert l'agrément à 75 % — vous êtes donc protégée d'un acquéreur indésirable." },
      { strong: "Au décès de vos parents", rest: ", leurs parts seront transmises selon les statuts et la dévolution successorale." },
    ],
  },

  faq: [
    { q: "Qu'est-ce qu'une SCI ?", a: "C'est une société civile dont l'objet est la détention et la gestion d'un patrimoine immobilier. Au lieu d'être propriétaire direct d'un immeuble, on détient des parts d'une société qui en est propriétaire." },
    { q: "Pourquoi créer une SCI ?", a: "Pour faciliter la transmission progressive d'un patrimoine immobilier, éviter l'indivision, organiser la gestion en couple ou en famille, et bénéficier d'un cadre juridique stable." },
    { q: "Quelle est la fiscalité d'une SCI ?", a: "Par défaut, la SCI relève de l'impôt sur le revenu (IR) : chaque associé est imposé sur sa quote-part des revenus. Elle peut opter pour l'impôt sur les sociétés (IS), parfois plus avantageux pour les locations meublées ou commerciales." },
    { q: "Comment céder des parts ?", a: "La cession se fait par acte écrit, souvent notarié. Elle est en général soumise à l'agrément des autres associés, dont les modalités sont fixées par les statuts. Une cession peut donner lieu à des droits d'enregistrement (5 % en général)." },
    { q: "Comment HÉRITA l'analyse-t-il ?", a: "HÉRITA lit les statuts, identifie la dénomination, les associés, le capital, le gérant, les clauses d'agrément, le régime fiscal et tout démembrement. Vous recevez en quelques minutes une lecture claire de votre place dans la SCI." },
    { q: "Que se passe-t-il au décès d'un associé ?", a: "Ses parts sont transmises à ses héritiers selon le droit commun des successions. Les statuts peuvent prévoir un agrément, un droit de préférence ou un rachat obligatoire. Sans clause particulière, les héritiers deviennent automatiquement associés." },
    { q: "Peut-on être gérant et associé ?", a: "Oui, c'est même la formule la plus courante en SCI familiale. Le gérant peut être un associé majoritaire ou minoritaire. Sa nomination et ses pouvoirs sont fixés par les statuts ou par décision d'assemblée." },
    { q: "Quelles sont les obligations annuelles ?", a: "Tenir une comptabilité (simplifiée si IR, complète si IS), réunir au moins une assemblée annuelle pour approuver les comptes, déclarer les revenus de la SCI à l'administration fiscale via le formulaire 2072." },
    { q: "Une SCI peut-elle acheter une résidence principale ?", a: "Oui, mais c'est rarement optimal fiscalement : on perd l'exonération de plus-value sur la résidence principale et l'abattement pour durée de détention est moins avantageux. À étudier au cas par cas." },
    { q: "Quel rapport avec l'indivision ?", a: "La SCI est souvent présentée comme une alternative à l'indivision. L'indivision est subie (à la suite d'une succession par exemple), la SCI est organisée. Les règles de décision et de cession y sont beaucoup plus claires." },
  ],

  finalCta: {
    title: "Vous avez reçu un document SCI\u00A0?",
    subtitle: "Déposez vos statuts, votre acte de cession ou votre procès-verbal d'assemblée. HÉRITA identifie automatiquement votre place dans la société, vos droits, vos obligations et la fiscalité applicable afin de vous fournir une lecture claire et personnalisée.",
  },
};

const Sci = () => <GuidePage config={config} />;
export default Sci;
